import { Maybe } from '@csp/csp-common-model';
import { SpinnerPage } from '@csp/csp-web-ui';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import { useOnMount } from '@csp/fe-hooks';
import { isEmpty } from 'lodash';
import { FC, PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, generatePath, useLocation } from 'react-router-dom';
import { CspPath } from '../../../CspPath';
import { useStudiesContext } from '../../../common/context/studies/useStudiesContext';
import { displayCloseoutTrackerInfoDialogSelector } from '../../account/ducks/displayCloseoutTrackerInfoDialogSelector';
import { useConsentContext } from '../../consent/context/hook/useConsentContext';
import { useCanApproveConsents } from '../../consent/hook/useCanApproveConsents';
import { FaultPageErrorType } from '../../fault/model/FaultPageErrorType';
import { useAuthenticatedRedirectContext } from '../../navigation/hooks/useAuthenticatedRedirectContext';
import {
  getAuthStateErrorSelector,
  getUserSelector,
  principalContextSelector,
} from '../../security/ducks/principalContextSelectors';
import { useStudyRedirect } from '../../studies/hook/useStudyRedirect';
import { NextSetupUrlParams } from '../model/NextSetupUrlParams';

export const getNextSetupUrl = ({
  hasPendingGenericConsents,
  hasPendingVideoTrainingConsents,
  hasPendingAnalyticsConsents,
  hasConfirmedAccount,
  shouldDisplayCloseoutTrackerInfoDialog,
  shouldSelectStudy,
}: NextSetupUrlParams): Maybe<CspPath> => {
  if (shouldSelectStudy) {
    return CspPath.STUDIES;
  } else if (hasPendingGenericConsents) {
    return CspPath.CONSENT;
  } else if (!hasConfirmedAccount) {
    return CspPath.CONFIRM_ACCOUNT;
  } else if (hasPendingVideoTrainingConsents) {
    return CspPath.VIDEO_TRAINING;
  } else if (hasPendingAnalyticsConsents) {
    return CspPath.CONSENT;
  } else if (shouldDisplayCloseoutTrackerInfoDialog) {
    return CspPath.CLOSEOUT_TRACKER_NOTIFICATION;
  } else {
    return undefined;
  }
};
export const UserSetup: FC<PropsWithChildren> = ({ children }) => {
  const {
    consents,
    error: consentsError,
    fetchConsents,
    hasPendingConsents,
    hasPendingVideoTrainingConsents,
    isLoading: isLoadingConsentApprovals,
  } = useConsentContext();
  const { selectedStudy } = useStudiesContext();
  const studiesSetup = useStudyRedirect();
  const location = useLocation();

  const user = useSelector(getUserSelector);
  const authError = useSelector(getAuthStateErrorSelector);
  const shouldDisplayCloseoutTrackerInfoDialog = useSelector(displayCloseoutTrackerInfoDialogSelector);
  const principalContext = useSelector(principalContextSelector);
  const canApproveConsents = useCanApproveConsents();

  const shouldApproveConsents = selectedStudy && user && canApproveConsents;
  const hasPermissionToSkipVideoTraining = process.env.NX_PUBLIC_SKIP_VIDEO_TRAINING === 'true';
  const isAnalyticsEnabled = !!selectedStudy?.features?.summary.webAnalytics.isEnabledOnAnySite;
  const isLoading = studiesSetup.loading || isLoadingConsentApprovals || principalContext.isLoading;
  const error = consentsError || authError;

  useEffect(() => {
    (async (): Promise<void> => {
      if (shouldApproveConsents && isEmpty(consents) && !consentsError) {
        await fetchConsents();
      }
    })();
  }, [consents, consentsError, fetchConsents, shouldApproveConsents]);

  const { canRedirect } = useAuthenticatedRedirectContext();

  if (error) {
    console.error(error);
    return (
      <Navigate replace to={generatePath(CspPath.ERROR, { type: FaultPageErrorType.INVALID_STUDY_CONFIGURATION })} />
    );
  }

  if (isLoading) {
    return <SpinnerPage />;
  }

  const nextSetupUrl = getNextSetupUrl({
    hasPendingGenericConsents: hasPendingConsents([ConsentType.EULA, ConsentType.PRIVACY_POLICY]),
    hasPendingVideoTrainingConsents: !hasPermissionToSkipVideoTraining && hasPendingVideoTrainingConsents(),
    hasPendingAnalyticsConsents: isAnalyticsEnabled && hasPendingConsents([ConsentType.ANALYTICS]),
    hasConfirmedAccount: !!user?.hasUserConsent(ConsentType.CONFIRM_ACCOUNT),
    shouldDisplayCloseoutTrackerInfoDialog,
    shouldSelectStudy: studiesSetup.shouldSelectStudy,
  });

  if (nextSetupUrl && nextSetupUrl !== location.pathname) {
    return <Navigate replace to={nextSetupUrl} />;
  } else if (!nextSetupUrl && canRedirect) {
    return <AuthenticatedRedirect />;
  }

  return <>{children}</>;
};

const AuthenticatedRedirect: FC = () => {
  const { startRedirect } = useAuthenticatedRedirectContext();
  useOnMount(() => {
    startRedirect();
  });

  return null;
};
